/**
 * What the DMP editor does when new content arrives (docgen reasons, round
 * 5c). See dmpEditorSync.test.ts for the regression this closes.
 *
 *   flush-then-load  a different section arrives while a save is pending:
 *                    save the section being left, with its own text, first;
 *   load             replace the editor's text;
 *   keep             the same section arrives (a listener echo) while the
 *                    user has unsaved changes: keep their text;
 *   none             nothing to do.
 */
export type EditorContentAction = 'flush-then-load' | 'load' | 'keep' | 'none';

/**
 * What the panel's section save reports (round 5g): 'saved' only after the
 * version check accepted the write; 'refused' when it was refused as changed
 * elsewhere (or blocked because the section already was); 'skipped' when
 * nothing was written for any other reason.
 */
export type SectionSaveResult = 'saved' | 'refused' | 'skipped';

export function editorContentAction(args: {
  incomingSectionId: string;
  loadedSectionId: string | null;
  pendingSave: boolean;
  incomingContent: string;
  editorHtml: string;
  /** What the editor holds (round 5f). Without it, only a pending save keeps. */
  editState?: EditorEditState | null;
}): EditorContentAction {
  const { incomingSectionId, loadedSectionId, pendingSave, incomingContent, editorHtml, editState } = args;
  if (incomingSectionId !== loadedSectionId) return pendingSave ? 'flush-then-load' : 'load';
  if (pendingSave) return 'keep';
  // Round 5f: the user changed this section and the incoming text is not
  // what the editor last sent. That is a remote edit, or the SDK rolling back
  // a refused write (it re-emits the stored document BEFORE the page learns
  // of the refusal, when nothing is pending any more). Either way, loading
  // would put the stored text over the user's: keep theirs.
  if (editState && editState.sectionId === incomingSectionId && editState.userChanged && incomingContent !== editState.baselineHtml) {
    return 'keep';
  }
  return incomingContent !== editorHtml ? 'load' : 'none';
}

/** What the editor tells the panel about the section it is editing (round 5d). */
export interface EditorEditState {
  /** The section whose text is in the editor. */
  sectionId: string;
  /** True while the user has typing not yet saved. */
  pendingSave: boolean;
  /** The HTML the editor last saved or loaded for that section. */
  baselineHtml: string;
  /** True once the user has changed this section's text since it was loaded (round 5e). */
  userChanged?: boolean;
  /** The user's text whose save was refused, kept for "Copy my text" (round 5f). */
  refusedHtml?: string | null;
  /**
   * True once the editor kept the user's text against an edit made elsewhere
   * that it did not take on, until the next load (round 5h; reported to the
   * panel since round 5i, so a section switch asks first).
   */
  keptForeign?: boolean;
}

/**
 * Should the page take on a section snapshot's version (round 5d)?
 *
 * The app's contract (utils/versionedDoc.ts) is that a page observes a
 * version ONLY where it takes on the stored content. While the user has
 * unsaved typing in a section, the editor keeps their text ('keep'), so a
 * snapshot of that section whose content differs from what the editor last
 * saved or loaded is a remote edit (another tab, a regeneration) the page has
 * NOT taken on: its version is not observed, the pending save claims the old
 * version plus one, the rules refuse it, and the user sees the "changed
 * elsewhere" message instead of silently overwriting the other edit. The
 * echo of the user's own save carries exactly the HTML last saved, so it is
 * observed as usual.
 */
export function shouldObserveSectionVersion(args: {
  sectionId: string;
  incomingContent: string;
  editState: EditorEditState | null;
}): boolean {
  const { sectionId, incomingContent, editState } = args;
  if (!editState || editState.sectionId !== sectionId) return true;
  // The page takes a snapshot on exactly when the editor does: not while it
  // keeps the user's text (a pending save, or user-changed text the store
  // does not hold, round 5f).
  if (!editState.pendingSave && !editState.userChanged) return true;
  return incomingContent === editState.baselineHtml;
}

/**
 * Carry out an editorContentAction in the one safe order (round 5d): save
 * the section being left FIRST (with its own text, its own flag and its own
 * section id), THEN reset the "user changed it" flag, THEN record the new
 * section as loaded, THEN load its text. Resetting the flag or moving the
 * section id before the flush is how typed text was lost, or saved into the
 * wrong section.
 */
export function applyContentAction(
  action: EditorContentAction,
  ops: { flush: () => void; resetUserChanged: () => void; setLoadedSection: () => void; load: () => void },
): void {
  if (action === 'keep' || action === 'none') return;
  if (action === 'flush-then-load') ops.flush();
  ops.resetUserChanged();
  ops.setLoadedSection();
  ops.load();
}

/**
 * What the editor holds, as one small state machine (round 5e). The editor
 * calls load() when it takes on a section's text, typed() when the user
 * changes it, and saved(html) when it sends a save; state() is what it
 * reports to the panel. saved() moving the baseline is what keeps the echo
 * of the user's own save from looking like a remote edit.
 */
export class EditorTracker {
  private s: Omit<Required<EditorEditState>, 'keptForeign'> = { sectionId: '', pendingSave: false, baselineHtml: '', userChanged: false, refusedHtml: null };

  /** The user's own text last sent for this section, or null; never a loaded text (round 5g). */
  private lastUserHtml: string | null = null;
  /** True once this section's save was refused, until a load. */
  private isRefused = false;
  /**
   * True once the editor KEPT the user's text against an incoming text that
   * differs from the baseline (a remote edit it did not take on), until a
   * load (round 5h). A confirmation of an earlier save then says nothing
   * about what the store holds, so it must not reset "user changed".
   */
  private keptForeign = false;

  load(sectionId: string, content: string): void {
    this.s = { sectionId, pendingSave: false, baselineHtml: content, userChanged: false, refusedHtml: null };
    this.lastUserHtml = null;
    this.isRefused = false;
    this.keptForeign = false;
  }

  typed(): void {
    this.s = { ...this.s, pendingSave: true, userChanged: true };
  }

  saved(html: string): void {
    this.lastUserHtml = html;
    // While the section is refused, every later (blocked) save is the
    // user's newest text: keep it as the text to copy.
    this.s = { ...this.s, pendingSave: false, baselineHtml: html, refusedHtml: this.isRefused ? html : this.s.refusedHtml };
  }

  /**
   * The section's save was refused as changed elsewhere: keep the user's own
   * text it carried (round 5f). Round 5g: only the user's text, never a
   * loaded one, so coming back to a stale section records nothing.
   */
  refused(): void {
    this.isRefused = true;
    if (this.lastUserHtml !== null) this.s = { ...this.s, refusedHtml: this.lastUserHtml };
  }

  /**
   * The panel confirmed that the save of `html` was written (round 5g).
   * "User changed" resets only when nothing newer is pending, the confirmed
   * text is what the editor last sent, and the section is not refused; a
   * remote edit after that loads, as for a section nobody changed.
   */
  confirmed(html: string): void {
    if (!this.s.pendingSave && this.s.baselineHtml === html && this.s.refusedHtml === null && !this.isRefused && !this.keptForeign) {
      this.s = { ...this.s, userChanged: false };
    }
  }

  /**
   * Reset to the generated text: the user's own action and one save, which
   * does NOT replace the refused text (round 5g); that stays copyable until
   * discard or reload.
   */
  resetTo(html: string): void {
    this.s = { ...this.s, pendingSave: false, userChanged: true, baselineHtml: html };
  }

  /**
   * The editor kept the user's text against `incomingContent` (a 'keep'
   * answer). A text other than the baseline is a remote one the page did
   * not take on: remember it until the next load (round 5h).
   */
  kept(incomingContent: string): void {
    if (incomingContent !== this.s.baselineHtml) this.keptForeign = true;
  }

  /** A pending save abandoned without being sent (the editor went away). */
  cancelled(): void {
    this.s = { ...this.s, pendingSave: false };
  }

  state(): Required<EditorEditState> {
    return { ...this.s, keptForeign: this.keptForeign };
  }
}

/**
 * May the editor apply the panel's confirmation of a save (round 5h)? Only a
 * 'saved' result, for the section still in the editor, and only when the
 * section's LATEST incoming text is the confirmed text: if a remote edit
 * arrived while the panel was finishing the save, the confirmation is about
 * a text the store no longer holds.
 */
export function shouldConfirmSave(args: {
  result: SectionSaveResult | void;
  sectionAtSave: string;
  loadedSectionId: string;
  latestIncomingContent: string;
  html: string;
}): boolean {
  return args.result === 'saved' && args.loadedSectionId === args.sectionAtSave && args.latestIncomingContent === args.html;
}

// ── A stale section: a save the version check refused (round 5e) ───────────

/** Shown on a section whose save was refused, for as long as it stays stale. */
export const STALE_SECTION_BANNER =
  'This section was changed elsewhere, so your changes are not being saved. Copy your text if you need it, then reload to see the current version.';
export const SWITCH_KEEP_LABEL = 'Keep editing';
export const SWITCH_DISCARD_LABEL = 'Discard my changes and switch';
/** The question asked before leaving a stale section that holds the user's text. */
export const SWITCH_CONFIRM_TEXT =
  'Your changes to this section were not saved, because it was changed elsewhere. If you switch now they will be lost.';

/**
 * The question asked before leaving a section whose latest changes were kept
 * against an edit made elsewhere (round 5i): the section is not stale yet,
 * but the save the switch would flush is about to be refused.
 */
export const SWITCH_CONFIRM_KEPT_TEXT =
  'Your latest changes to this section have not been saved, because it was changed elsewhere. If you switch now they will be lost.';

/**
 * May the user switch from one section to another now? 'confirm' when the
 * editor holds text the user changed in the section being left AND either
 * that section is stale (a save was refused) or the editor kept the user's
 * text against an edit made elsewhere (round 5i). In both cases switching
 * would replace the user's text with no warning: the flushed save of a kept
 * section is refused, because the page never took on the remote version.
 */
export function sectionSwitchGuard(args: {
  fromSectionId: string | null;
  toSectionId: string;
  staleSections: ReadonlySet<string>;
  editState: EditorEditState | null;
}): 'allow' | 'confirm' {
  const { fromSectionId, toSectionId, staleSections, editState } = args;
  if (!fromSectionId || fromSectionId === toSectionId) return 'allow';
  if (!editState || editState.sectionId !== fromSectionId) return 'allow';
  if (!staleSections.has(fromSectionId) && !editState.keptForeign) return 'allow';
  return editState.userChanged || editState.pendingSave ? 'confirm' : 'allow';
}

/** The wording of the switch question: a stale section's, or a kept one's (round 5i). */
export function switchConfirmText(args: { fromSectionId: string | null; staleSections: ReadonlySet<string> }): string {
  return args.fromSectionId && args.staleSections.has(args.fromSectionId) ? SWITCH_CONFIRM_TEXT : SWITCH_CONFIRM_KEPT_TEXT;
}

/** The stale set without one section: on discard, that section only. */
export function clearStaleSection(staleSections: ReadonlySet<string>, sectionId: string): Set<string> {
  const next = new Set(staleSections);
  next.delete(sectionId);
  return next;
}

/**
 * Should leaving the page ask the browser to confirm? Only when something
 * would be lost: a save still pending, or a stale section holding the user's
 * changed text. Nothing pending and nothing refused: leave without asking.
 */
export function shouldPromptBeforeUnload(args: { pendingSave: boolean; stale: boolean; userChanged: boolean }): boolean {
  return args.pendingSave || (args.stale && args.userChanged);
}

// ── Copy and discard (round 5f) ────────────────────────────────────────────

/** The stale banner's button. */
export const COPY_MY_TEXT_LABEL = 'Copy my text';

/** The user's kept HTML as plain text for the clipboard: one line per block. */
export function plainTextOf(html: string): string {
  return String(html ?? '')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|h[1-6]|li|div|tr|blockquote)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/**
 * "Discard my changes and switch", in the one safe order: cancel the pending
 * save FIRST (so the switch that follows has nothing to flush, and the
 * discarded text is never saved over the other edit), then clear that
 * section's stale state, then give it a fresh version tracker, then switch.
 */
export function applyDiscard(ops: {
  cancelPending: () => void;
  clearStale: () => void;
  freshTracker: () => void;
  switchSection: () => void;
}): void {
  ops.cancelPending();
  ops.clearStale();
  ops.freshTracker();
  ops.switchSection();
}

/**
 * Is an editor update the user typing (round 5g)? Not while the editor is
 * loading text itself, and not when the document did not change: tiptap's
 * setEditable (a read-only toggle) emits an update with no document change,
 * which would otherwise mark "user changed" on mount and could write a false
 * "edited" save when the edit lock clears.
 */
export function isUserEditUpdate(a: { settingContent: boolean; docChanged: boolean }): boolean {
  return !a.settingContent && a.docChanged;
}
